import React from 'react';
import { useTranslation } from 'react-i18next';
import { CalendarCheck, CalendarClock, CalendarDays } from 'lucide-react';
import { VacationBalance } from '../../types';
import { VacationSummaryCard } from './VacationSummaryCard';

interface VacationBalanceCardsProps {
  balance: VacationBalance | null | undefined;
  loading?: boolean;
}

/**
 * Vacation overview summary: three tiles (available / used / pending). The
 * available tile also shows a progress bar of used days vs. the annual
 * allowance. All tiles reuse a single {@link VacationSummaryCard} component.
 */
export const VacationBalanceCards: React.FC<VacationBalanceCardsProps> = ({ balance, loading }) => {
  const { t } = useTranslation('vacations');

  if (loading) {
    return (
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3" data-testid="vacation-balance-loading">
        {[0, 1, 2].map((i) => (
          <div key={i} className="h-28 animate-pulse rounded-xl bg-muted" />
        ))}
      </div>
    );
  }

  // A missing balance row (backend 404 → null) still renders the three cards
  // zeroed, rather than a dashed empty message, so the layout stays stable and
  // the member can see their (empty) breakdown before the first request.
  const used = balance?.used ?? 0;
  const pending = balance?.pending ?? 0;
  const allowance = (balance?.annualAllowance ?? 0) + (balance?.carriedOver ?? 0);
  const isUnlimited = !!balance?.unlimited;
  const usedPercent = allowance > 0 ? Math.min(100, (used / allowance) * 100) : 0;
  const pendingPercent = allowance > 0 ? Math.min(100 - usedPercent, (pending / allowance) * 100) : 0;
  // FLEX (and any uncapped) balances have no accrued allowance, so `available`
  // can be negative; never surface a negative remaining count. The "/ N days"
  // suffix is only meaningful when there is an actual allowance to compare to.
  const remainingValue = Math.max(0, balance?.available ?? 0);

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-3" data-testid="vacation-balance-cards">
      <VacationSummaryCard
        label={t('balance.remaining')}
        value={isUnlimited ? t('balance.unlimited') : remainingValue}
        valueSuffix={
          !isUnlimited && allowance > 0 ? t('balance.allowanceSuffix', { allowance }) : undefined
        }
        hint={balance === null ? t('balance.notInitialized') : undefined}
        icon={<CalendarCheck />}
        accent="primary"
        segments={[
          { key: 'used', percent: usedPercent, className: 'bg-prosigliere-blue' },
          { key: 'pending', percent: pendingPercent, className: 'bg-orange-500' },
        ]}
        legend={[
          {
            key: 'approved',
            label: t('balance.approved'),
            value: t('balance.days', { count: used }),
            dotClassName: 'bg-prosigliere-blue',
          },
          {
            key: 'pending',
            label: t('balance.pendingApproval'),
            value: t('balance.days', { count: pending }),
            dotClassName: 'bg-orange-500',
          },
        ]}
        testId="vacation-balance-remaining"
      />
      <VacationSummaryCard
        label={t('balance.used')}
        value={used}
        hint={t('balance.usedHint', { allowance })}
        icon={<CalendarDays />}
        testId="vacation-balance-used"
      />
      <VacationSummaryCard
        label={t('balance.pending')}
        value={pending}
        hint={t('balance.pendingHint')}
        icon={<CalendarClock />}
        accent="warning"
        testId="vacation-balance-pending"
      />
    </div>
  );
};
